import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { Datagrid, Identifier, List, TextField, useCreate, useNotify, useTranslate, useUnselectAll } from 'react-admin';
import LoadingButton from '@mui/lab/LoadingButton';
import { useRecordSelection } from 'react-admin';
import useGetAvatar from '@/hooks/useGetAvatar';
import { composeUri } from '@/utils/compose';
import { AVATARS, AVATARS_MOVE_TO, CLIENT_METADATA_AVATAR_TYPE_NAME, NAVIGATION_ROUTE, SITES } from '@/utils/CONST';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import tableRowSx from '@/utils/table-row-sx';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import { nth } from 'lodash';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import ConfirmDialog from '@/components/common/confirm-dialog/confirm-dialog';

const EditSiteParentDialog = () => {
    const translate = useTranslate();
    const { clientId, siteId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const { avatar: site, isLoading: isLoadingSite } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, siteId ?? ''),
        queryKey: `${SITES}_${clientId}`,
    });
    const [selectedAvatarIds, { select }] = useRecordSelection({
        resource: AVATARS,
        storeKey: 'clientsParentsList',
    });
    const unselectAll = useUnselectAll(AVATARS);
    const [confirmDialogState, setConfirmDialogState] = useState<boolean>(false);

    const onRowClick: any = (id: Identifier) => {
        unselectAll();
        select([id]);
    };

    const selectedAvatarUriId = nth(selectedAvatarIds, 0);

    const { avatar: client, isLoading: isLoadingClient } = useGetAvatar({
        avatarUriId: selectedAvatarUriId,
    });

    useEffect(() => {
        select([composeUri(AVATARS, clientId)]);
    }, []);

    const navigateToNavigation = () => {
        navigate(`/${NAVIGATION_ROUTE}`);
    };

    const handleSubmit = () => {
        create(
            AVATARS,
            {
                data: {
                    child: site?.id,
                    moveTo: selectedAvatarUriId,
                },
                meta: {
                    realResource: AVATARS_MOVE_TO,
                },
            },
            {
                onSuccess: () => {
                    unselectAll();
                    navigateToNavigation();
                    notify('ra.notification.updated', {
                        type: 'success',
                        messageArgs: {
                            smart_count: 1,
                        },
                    });
                },
            },
        );
    };

    const handleConfirmDialogOpen = () => {
        setConfirmDialogState(true);
    };

    const handleConfirmDialogFalse = () => {
        setConfirmDialogState(false);
    };

    return (
        <DialogTemplate
            icon={<ChangeCircleOutlinedIcon />}
            title={translate('main.navigation.change_client_dialog_title')}
            open={true}
            onClose={navigateToNavigation}
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                }}
            >
                <ListStylesContainer>
                    <List
                        actions={false}
                        component='div'
                        resource={AVATARS}
                        filter={{
                            'metadataAvatarType.name': CLIENT_METADATA_AVATAR_TYPE_NAME,
                            enabled: true,
                        }}
                        sort={{
                            order: 'DESC',
                            field: 'createdAt',
                        }}
                        empty={false}
                        storeKey='clientsParentsList'
                        disableSyncWithLocation
                    >
                        <Datagrid bulkActionButtons={false} rowClick={onRowClick} rowSx={tableRowSx(selectedAvatarIds)}>
                            <TextField label={'main.searchClient'} source='name' />
                        </Datagrid>
                    </List>
                </ListStylesContainer>

                <ButtonsBlock>
                    <Button
                        onClick={navigateToNavigation}
                        disabled={isLoadingCreate || isLoadingSite || isLoadingClient}
                    >
                        {translate('buttons.cancel')}
                    </Button>

                    <LoadingButton
                        variant='contained'
                        loading={isLoadingCreate || isLoadingSite || isLoadingClient}
                        onClick={handleConfirmDialogOpen}
                    >
                        {translate('buttons.validate')}
                    </LoadingButton>
                </ButtonsBlock>
            </Box>

            {confirmDialogState && (
                <ConfirmDialog
                    open={confirmDialogState}
                    onCancelClick={handleConfirmDialogFalse}
                    onOkClick={handleSubmit}
                    isLoading={isLoadingCreate || isLoadingSite || isLoadingClient}
                    icon={<ChangeCircleOutlinedIcon />}
                    label={`Vous etes sur le point de transferer le site "${site?.name}" vers le client "${client?.name}"`}
                />
            )}
        </DialogTemplate>
    );
};

export default EditSiteParentDialog;
